import React from 'react';
import Pagination from '../../../../../components/Pagination';
import { StaffStatus } from '../../hooks/useStaffList';
import { useManageStaffStore } from '../../store/useManageStaffStore';
import { ComponentReportingToolsStaffStatus } from '@/graphql/generated/graphql';

export const FALLBACK_LABEL: Record<StaffStatus, string> = {
  pending_invite: 'Pending invite',
  invited_pending_signup: 'Invited — pending signup',
  registered_pending_onboarding: 'Registered — onboarding pending',
  registered_onboarded: 'Active — onboarding complete',
};

const FALLBACK_COLOR: Record<StaffStatus, string> = {
  pending_invite: '#9CA3AF',
  invited_pending_signup: '#F59E0B',
  registered_pending_onboarding: '#3B82F6',
  registered_onboarded: '#22C55E',
};

function getStatusLabel(status: StaffStatus, cms?: ComponentReportingToolsStaffStatus) {
  switch (status) {
    case 'pending_invite':
      return cms?.pendingInviteMessage || FALLBACK_LABEL[status];
    case 'invited_pending_signup':
      return cms?.invitedPendingSignUpMessage || FALLBACK_LABEL[status];
    case 'registered_pending_onboarding':
      return cms?.registeredPendingOnboardingMessage || FALLBACK_LABEL[status];
    case 'registered_onboarded':
      return cms?.registeredOnboardedMessage || FALLBACK_LABEL[status];
    default:
      return FALLBACK_LABEL[status];
  }
}

function getStatusColor(status: StaffStatus, cms?: ComponentReportingToolsStaffStatus) {
  switch (status) {
    case 'pending_invite':
      return cms?.pendingInviteColor || FALLBACK_COLOR[status];
    case 'invited_pending_signup':
      return cms?.invitedPendingSignUpColor || FALLBACK_COLOR[status];
    case 'registered_pending_onboarding':
      return cms?.registeredPendingOnboardingColor || FALLBACK_COLOR[status];
    case 'registered_onboarded':
      return cms?.registeredOnboardedColor || FALLBACK_COLOR[status];
    default:
      return FALLBACK_COLOR[status];
  }
}

const StatusText: React.FC<{ status: StaffStatus; statusConfig?: ComponentReportingToolsStaffStatus }> = ({
  status,
  statusConfig,
}) => {
  const colorHex = getStatusColor(status, statusConfig);
  const label = getStatusLabel(status, statusConfig);

  return (
    <span className="text-cj-gray-700 inline-flex items-center gap-2 text-sh-xxs">
      <span className="inline-block min-h-2 min-w-2 rounded-full" style={{ backgroundColor: colorHex }} aria-hidden />
      {label}
    </span>
  );
};

type AllSchoolUsersListProps = {
  staff: Array<{ surname: string | null; forename: string | null; email: string; status: StaffStatus }>;
  totalPages: number;
  currentPage: number;
  handlePageChange: (page: number) => void;
  title?: React.ReactNode;
};

export const AllSchoolUsersList: React.FC<AllSchoolUsersListProps> = ({
  staff,
  totalPages,
  currentPage,
  handlePageChange,
  title = '',
}) => {
  const staffStatus = useManageStaffStore((state) => state?.page?.staffStatus) as
    | ComponentReportingToolsStaffStatus
    | undefined;

  return (
    <div className="border-cj-gray-200 col-span-12 mt-8 overflow-hidden rounded-xl border bg-white shadow-sm">
      {title && (
        <div className="border-cj-gray-200 flex items-center justify-between border-b px-6 py-5">
          {typeof title === 'string' ? <h2 className="text-sh-sm font-semibold text-black">{title}</h2> : title}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full table-auto">
          <thead>
            <tr>
              <th className="border-cj-gray-200 bg-cj-gray-50 text-cj-gray-500 border-b px-6 py-3 text-left text-sh-xxxs font-medium">
                Surname
              </th>
              <th className="border-cj-gray-200 bg-cj-gray-50 text-cj-gray-500 border-b px-6 py-3 text-left text-sh-xxxs font-medium">
                Name
              </th>
              <th className="border-cj-gray-200 bg-cj-gray-50 text-cj-gray-500 border-b px-6 py-3 text-left text-sh-xxxs font-medium">
                Email
              </th>
              <th className="border-cj-gray-200 bg-cj-gray-50 text-cj-gray-500 border-b px-6 py-3 text-left text-sh-xxxs font-medium">
                Status
              </th>
            </tr>
          </thead>

          <tbody>
            {staff.map((row, index) => (
              <tr key={index} className="border-cj-gray-200 border-b last:border-b-0 odd:bg-white even:bg-white">
                <td className="text-cj-gray-600 px-6 py-6 text-left text-sh-xxs">{row.surname ?? '—'}</td>
                <td className="text-cj-gray-600 px-6 py-6 text-left text-sh-xxs">{row.forename ?? '—'}</td>
                <td className="text-cj-gray-600 px-6 py-6 text-left text-sh-xxs">{row.email}</td>
                <td className="px-6 py-6 text-left">
                  <StatusText status={row.status} statusConfig={staffStatus} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="border-cj-gray-200 border-t bg-white px-4 py-3">
          <div className="flex w-full justify-center">
            <Pagination
              currentPage={currentPage}
              pageSize={totalPages}
              onChangePage={(page: number) => handlePageChange(page)}
            />
          </div>
        </div>
      )}
    </div>
  );
};
